/*
 *   File : shop.js
 *   Author : https://evoqins.com
 *   Description : Shops and sales component
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';

//Custom component
import { DataTableComponent } from '../../../Component/DataTable';
import { SummaryCard } from '../../../Component/Cards';
import { APIService } from '../../../Service';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { Loader } from '../../../Component/Others';

const ITEMS_PER_PAGE = 15;

const PAGE_SORT_FIELDS = [
    'shop',
    'sale_specialist',
    'total_sales',
];

const Shop = (props) => {

    const tableRef = useRef(null);
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [shopData, setShopData] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getShopData();
    }, [currentPage]);

    // handle page change of table
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setLoading(true);

        if (tableRef.current) {
            tableRef.current.scrollIntoView({ behavior: 'smooth', block: "start" });
        }
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PAGE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoading(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getShopData(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const TABLE_COLUMNS = [
        {
            name: 'Shop name',
            id: 'shop',
            sortField: 'shop',
            selector: row => row.shop,
            width: "260px",
            sortable: true,
        },
        {
            name: 'Sales Specialist',
            id: 'sale_specialist',
            sortField: 'sale_specialist',
            selector: row => row.sale_specialist,
            width: "160px",
            sortable: true,
        },
        {
            name: 'Total sales',
            id: 'total_sales',
            sortField: 'total_sales',
            selector: row => row.total_sales,
            width: "160px",
            sortable: true,
        },
        {
            name: 'Premium sales',
            selector: row => row.premium,
            width: "160px",
        },
        {
            name: 'Painters visited',
            selector: row => row.painter_visited === null ?
                "-"
                :
                row.painter_visited,
            width: "160px"

        },

    ];

    // API - Get shop and sale data
    const _getShopData = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        const effective_sort_by = sort_by && PAGE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'country-admin/shops-sale';
        let data = {
            "admin_id": props.adminId,
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setShopData(response.data);
            } else { }
            setLoading(false);
        });
    };

    return (
        <>
            {loading ?
                <div className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                    {/* loader */}
                    <Loader />
                </div>
                :
                <>
                    {/* Summary  */}
                    <div className='e-admin-box-shadow p-4 e-bg-white e-border-radius-16'>
                        <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                            Summary
                        </h4>
                        <div className='row row-gap-3 row-cols-xl-5 row-cols-lg-4 row-cols-md-4 row-cols-sm-3 row-cols-2 padding-10px-top'>

                            <div className='col'>
                                <SummaryCard title="Total shops"
                                    quantity={shopData.total_store} />
                            </div>

                            <div className='col'>
                                <SummaryCard title="Total sales"
                                    quantity={shopData.total_sales} />
                            </div>
                            <div className='col'>
                                <SummaryCard title="Premium sales"
                                    quantity={shopData.premium_sales} />
                            </div>
                            {/* <div className='col'>
                        <SummaryCard title="Painters visited"
                            quantity="10" />
                    </div> */}
                        </div>
                    </div>

                    {/* Shops and sales table */}
                    <div ref={tableRef} className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                        <div className='px-4'>
                            <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-600 line-height-28px'>
                                Shops and sales
                            </h4>
                        </div>

                        {/* Data table */}
                        {shopData.data.length !== 0 ?
                            <div className='e-table'>
                                <DataTableComponent
                                    key={`shops-sale-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                    columns={TABLE_COLUMNS}
                                    pagination={true}
                                    data={shopData.data}
                                    paginationServer={true}
                                    paginationPerPage={ITEMS_PER_PAGE}
                                    paginationTotalRows={shopData.total_count}
                                    paginationDefaultPage={currentPage}
                                    paginationResetDefaultPage={false}
                                    sortServer={true}
                                    defaultSortFieldId={sortField}
                                    defaultSortAsc={sortDirection !== 'desc'}
                                    onSort={_handleSort}
                                    onChangePage={_handlePageChange} />
                            </div>
                            :
                            // empty screen
                            <div className='h-100vh pt-5 mt-5'>
                                <EmptyScreen message="No data found" />
                            </div>
                        }

                    </div>
                </>
            }
        </>
    )
}

export default Shop;
